import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faMugHot,
    faPowerOff,
    faSpinner,
    faSeedling,
    faCapsules,
    faStar,
    faCrown,
    faHourglassHalf,
    faPaperPlane,
} from '@fortawesome/free-solid-svg-icons';
import { useConfirm } from '../../ui/components/ConfirmDialog';
import {
    listMyMachines,
    registerMachine,
    deactivateMachine,
    RegisterMachinePayload,
    listMyBeans,
    registerBean,
    deactivateBean,
    RegisterBeanPayload,
    listMyCapsules,
    registerCapsule,
    deactivateCapsule,
    RegisterCapsulePayload,
    getCoffeeMasterStatus,
    requestCoffeeMaster,
    CoffeeMasterStatus,
} from '../coffee_card_web/api/coffeeApi';
import {
    CoffeeMachine,
    CoffeeMachineType,
    CoffeeBean,
    CoffeeCapsule,
    CoffeeRoastLevel,
} from '../coffee_card_web/CoffeeCardWeb/types';
import './MachineManager.css';

// Coffee Master is a verified-owner capability on the backend User model. It is NOT in the
// JWT / auth slice, so we read it via GET /coffee/v2/coffee-master/me on mount: when
// isCoffeeMaster is true we render the full setup (machines + beans + capsules); otherwise we
// show the request panel. A 401/403 falls back to a graceful "request access" state.
type MasterState = 'checking' | 'master' | 'guest' | 'error';

type SetupTab = 'machines' | 'beans' | 'capsules';

const TYPE_OPTIONS: { value: CoffeeMachineType; label: string; hint: string }[] = [
    { value: 'manual', label: 'Manual', hint: 'Espresso, moka, pour-over, French press…' },
    { value: 'capsule', label: 'Capsule', hint: 'Nespresso, Dolce Gusto, Lavazza…' },
    { value: 'other', label: 'Other', hint: 'Anything else (matcha whisk, kettle…)' },
];

const ROAST_OPTIONS: { value: CoffeeRoastLevel; label: string }[] = [
    { value: 'light', label: 'Light' },
    { value: 'medium-light', label: 'Medium-light' },
    { value: 'medium', label: 'Medium' },
    { value: 'medium-dark', label: 'Medium-dark' },
    { value: 'dark', label: 'Dark' },
    { value: 'other', label: 'Other' },
];

// ---- machine form ----

const EMPTY_MACHINE_FORM = {
    type: 'manual' as CoffeeMachineType,
    brand: '',
    model: '',
    // manual
    grind: '',
    doseGrams: '',
    // capsule
    capsuleBrand: '',
    intensity: '',
    // other / free notes
    notes: '',
};

type MachineFormState = typeof EMPTY_MACHINE_FORM;

// Build the flexible `defaultBrew` config from the per-type form fields, omitting blanks.
function buildDefaultBrew(form: MachineFormState): Record<string, unknown> | undefined {
    const brew: Record<string, unknown> = {};
    if (form.type === 'manual') {
        brew.method = 'manual';
        if (form.grind.trim()) brew.grind = form.grind.trim();
        if (form.doseGrams.trim() !== '') {
            const dose = Number(form.doseGrams);
            if (!Number.isNaN(dose)) brew.doseGrams = dose;
        }
    } else if (form.type === 'capsule') {
        brew.method = 'capsule';
        if (form.capsuleBrand.trim()) brew.capsuleBrand = form.capsuleBrand.trim();
        if (form.intensity.trim() !== '') {
            const intensity = Number(form.intensity);
            brew.intensity = Number.isNaN(intensity) ? form.intensity.trim() : intensity;
        }
    } else if (form.notes.trim()) {
        brew.notes = form.notes.trim();
    }
    // `method` alone (no real config) is not worth persisting.
    const meaningful = Object.keys(brew).filter((k) => k !== 'method');
    return meaningful.length > 0 ? brew : undefined;
}

// ---- bean form ----

const EMPTY_BEAN_FORM = {
    name: '',
    roaster: '',
    origin: '',
    roastLevel: '' as CoffeeRoastLevel | '',
    process: '',
    notes: '',
};
type BeanFormState = typeof EMPTY_BEAN_FORM;

// ---- capsule form ----

const EMPTY_CAPSULE_FORM = {
    brand: '',
    name: '',
    intensity: '',
    notes: '',
};
type CapsuleFormState = typeof EMPTY_CAPSULE_FORM;

// Subtle derived-score chip shared by bean + capsule cards. Beans/capsules are never rated
// directly — the score is the aggregate of the home coffees logged with them.
const ScorePill: React.FC<{ avgRating: number | null; ratingCount: number }> = ({ avgRating, ratingCount }) => {
    if (!ratingCount || avgRating == null) {
        return <span className="mm-score mm-score--empty">not rated yet</span>;
    }
    return (
        <span className="mm-score">
            <FontAwesomeIcon icon={faStar} /> {avgRating.toFixed(1)} · {ratingCount} rating{ratingCount === 1 ? '' : 's'}
        </span>
    );
};

const MachineManager: React.FC = () => {
    const confirm = useConfirm();
    const [masterState, setMasterState] = useState<MasterState>('checking');
    const [status, setStatus] = useState<CoffeeMasterStatus | null>(null);
    const [tab, setTab] = useState<SetupTab>('machines');

    // ---- master / request flow ----
    const [requesting, setRequesting] = useState(false);
    const [requestMessage, setRequestMessage] = useState('');

    // ---- collections ----
    const [machines, setMachines] = useState<CoffeeMachine[]>([]);
    const [beans, setBeans] = useState<CoffeeBean[]>([]);
    const [capsules, setCapsules] = useState<CoffeeCapsule[]>([]);

    // ---- forms ----
    const [machineForm, setMachineForm] = useState<MachineFormState>(EMPTY_MACHINE_FORM);
    const [beanForm, setBeanForm] = useState<BeanFormState>(EMPTY_BEAN_FORM);
    const [capsuleForm, setCapsuleForm] = useState<CapsuleFormState>(EMPTY_CAPSULE_FORM);

    // ---- per-section submitting / deactivating state ----
    const [submittingMachine, setSubmittingMachine] = useState(false);
    const [submittingBean, setSubmittingBean] = useState(false);
    const [submittingCapsule, setSubmittingCapsule] = useState(false);
    const [deactivatingId, setDeactivatingId] = useState<string | null>(null);

    const loadSetup = useCallback(async () => {
        try {
            const data = await getCoffeeMasterStatus();
            setStatus(data);
            if (data.isCoffeeMaster) {
                setMasterState('master');
                const [m, b, c] = await Promise.all([listMyMachines(), listMyBeans(), listMyCapsules()]);
                setMachines(m);
                setBeans(b);
                setCapsules(c);
            } else {
                setMasterState('guest');
            }
        } catch (err) {
            const httpStatus = (err as { response?: { status?: number } })?.response?.status;
            // 401/403 — not authenticated as a master. Fall back gracefully to the request panel.
            if (httpStatus === 401 || httpStatus === 403) {
                setStatus(null);
                setMasterState('guest');
            } else {
                setMasterState('error');
            }
        }
    }, []);

    useEffect(() => {
        loadSetup();
    }, [loadSetup]);

    // ---------- request access ----------

    const handleRequestAccess = async () => {
        setRequesting(true);
        try {
            const res = await requestCoffeeMaster(requestMessage.trim() || undefined);
            const nextStatus = (res.status as NonNullable<CoffeeMasterStatus['request']>['status']) || 'pending';
            setStatus({
                isCoffeeMaster: false,
                request: {
                    status: nextStatus,
                    createdAt: new Date().toISOString(),
                    reviewedAt: null,
                },
            });
            setRequestMessage('');
            toast.success('Request sent — the admin will review it.');
        } catch (err) {
            const message =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                'Failed to send request.';
            toast.error(message);
        } finally {
            setRequesting(false);
        }
    };

    // ---------- machine handlers ----------

    const setMachineField = (key: keyof MachineFormState, value: string) =>
        setMachineForm((prev) => ({ ...prev, [key]: value }));

    const handleMachineSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!machineForm.brand.trim() || !machineForm.model.trim()) {
            toast.error('Brand and model are required.');
            return;
        }
        const payload: RegisterMachinePayload = {
            type: machineForm.type,
            brand: machineForm.brand.trim(),
            model: machineForm.model.trim(),
        };
        const defaultBrew = buildDefaultBrew(machineForm);
        if (defaultBrew) payload.defaultBrew = defaultBrew;

        setSubmittingMachine(true);
        try {
            const created = await registerMachine(payload);
            setMachines((prev) => [created, ...prev]);
            setMachineForm(EMPTY_MACHINE_FORM);
            toast.success(`${created.brand} ${created.model} registered.`);
        } catch (err) {
            const message =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                'Failed to register machine.';
            toast.error(message);
        } finally {
            setSubmittingMachine(false);
        }
    };

    const handleDeactivateMachine = async (machine: CoffeeMachine) => {
        const ok = await confirm({
            title: 'Deactivate this machine?',
            message: `${machine.brand} ${machine.model} will be hidden from new ratings. Existing ratings keep their reference.`,
            confirmLabel: 'Deactivate',
            tone: 'danger',
        });
        if (!ok) return;

        setDeactivatingId(machine._id);
        try {
            const updated = await deactivateMachine(machine._id);
            setMachines((prev) => prev.map((m) => (m._id === machine._id ? updated : m)));
            toast.success('Machine deactivated.');
        } catch (err) {
            const message =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                'Failed to deactivate machine.';
            toast.error(message);
        } finally {
            setDeactivatingId(null);
        }
    };

    // ---------- bean handlers ----------

    const setBeanField = (key: keyof BeanFormState, value: string) =>
        setBeanForm((prev) => ({ ...prev, [key]: value }));

    const handleBeanSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!beanForm.name.trim()) {
            toast.error('Bean name is required.');
            return;
        }
        const payload: RegisterBeanPayload = { name: beanForm.name.trim() };
        if (beanForm.roaster.trim()) payload.roaster = beanForm.roaster.trim();
        if (beanForm.origin.trim()) payload.origin = beanForm.origin.trim();
        if (beanForm.roastLevel) payload.roastLevel = beanForm.roastLevel;
        if (beanForm.process.trim()) payload.process = beanForm.process.trim();
        if (beanForm.notes.trim()) payload.notes = beanForm.notes.trim();

        setSubmittingBean(true);
        try {
            const created = await registerBean(payload);
            setBeans((prev) => [created, ...prev]);
            setBeanForm(EMPTY_BEAN_FORM);
            toast.success(`${created.name} added.`);
        } catch (err) {
            const message =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                'Failed to register bean.';
            toast.error(message);
        } finally {
            setSubmittingBean(false);
        }
    };

    const handleDeactivateBean = async (bean: CoffeeBean) => {
        const ok = await confirm({
            title: 'Deactivate this bean?',
            message: `${bean.name} will be hidden from new ratings. Existing ratings keep their reference.`,
            confirmLabel: 'Deactivate',
            tone: 'danger',
        });
        if (!ok) return;

        setDeactivatingId(bean._id);
        try {
            const updated = await deactivateBean(bean._id);
            setBeans((prev) => prev.map((b) => (b._id === bean._id ? updated : b)));
            toast.success('Bean deactivated.');
        } catch (err) {
            const message =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                'Failed to deactivate bean.';
            toast.error(message);
        } finally {
            setDeactivatingId(null);
        }
    };

    // ---------- capsule handlers ----------

    const setCapsuleField = (key: keyof CapsuleFormState, value: string) =>
        setCapsuleForm((prev) => ({ ...prev, [key]: value }));

    const handleCapsuleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!capsuleForm.brand.trim() || !capsuleForm.name.trim()) {
            toast.error('Brand and name are required.');
            return;
        }
        const payload: RegisterCapsulePayload = {
            brand: capsuleForm.brand.trim(),
            name: capsuleForm.name.trim(),
        };
        if (capsuleForm.intensity.trim() !== '') {
            const intensity = Number(capsuleForm.intensity);
            if (!Number.isNaN(intensity)) payload.intensity = intensity;
        }
        if (capsuleForm.notes.trim()) payload.notes = capsuleForm.notes.trim();

        setSubmittingCapsule(true);
        try {
            const created = await registerCapsule(payload);
            setCapsules((prev) => [created, ...prev]);
            setCapsuleForm(EMPTY_CAPSULE_FORM);
            toast.success(`${created.brand} ${created.name} added.`);
        } catch (err) {
            const message =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                'Failed to register capsule.';
            toast.error(message);
        } finally {
            setSubmittingCapsule(false);
        }
    };

    const handleDeactivateCapsule = async (capsule: CoffeeCapsule) => {
        const ok = await confirm({
            title: 'Deactivate this capsule?',
            message: `${capsule.brand} ${capsule.name} will be hidden from new ratings. Existing ratings keep their reference.`,
            confirmLabel: 'Deactivate',
            tone: 'danger',
        });
        if (!ok) return;

        setDeactivatingId(capsule._id);
        try {
            const updated = await deactivateCapsule(capsule._id);
            setCapsules((prev) => prev.map((c) => (c._id === capsule._id ? updated : c)));
            toast.success('Capsule deactivated.');
        } catch (err) {
            const message =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                'Failed to deactivate capsule.';
            toast.error(message);
        } finally {
            setDeactivatingId(null);
        }
    };

    // ---------- derived ----------

    const activeMachines = useMemo(() => machines.filter((m) => m.isActive), [machines]);
    const inactiveMachines = useMemo(() => machines.filter((m) => !m.isActive), [machines]);
    const activeBeans = useMemo(() => beans.filter((b) => b.isActive), [beans]);
    const inactiveBeans = useMemo(() => beans.filter((b) => !b.isActive), [beans]);
    const activeCapsules = useMemo(() => capsules.filter((c) => c.isActive), [capsules]);
    const inactiveCapsules = useMemo(() => capsules.filter((c) => !c.isActive), [capsules]);

    const activeCount =
        tab === 'machines' ? activeMachines.length : tab === 'beans' ? activeBeans.length : activeCapsules.length;

    // ---------- render: loading / error ----------

    if (masterState === 'checking') {
        return (
            <div className="machine-manager machine-manager--centered">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>Loading your coffee setup…</span>
            </div>
        );
    }

    if (masterState === 'error') {
        return (
            <div className="machine-manager machine-manager--centered">
                <p>Couldn't load your coffee setup.</p>
                <button
                    className="mm-btn"
                    type="button"
                    onClick={() => {
                        setMasterState('checking');
                        loadSetup();
                    }}
                >
                    Retry
                </button>
            </div>
        );
    }

    // ---------- render: guest (request access) ----------

    if (masterState === 'guest') {
        const reqStatus = status?.request?.status ?? null;
        const isPending = reqStatus === 'pending';
        const isRejected = reqStatus === 'rejected';

        return (
            <div className="machine-manager">
                <header className="mm-header">
                    <h2>
                        <FontAwesomeIcon icon={faCrown} /> Coffee Master
                    </h2>
                </header>

                <div className="mm-card mm-intro">
                    <p className="mm-intro-lead">
                        Become a <strong>Coffee Master</strong> to register your machines, beans &amp; capsules — and rate
                        the coffees you brew at home with full detail.
                    </p>
                    <ul className="mm-intro-list">
                        <li>
                            <FontAwesomeIcon icon={faMugHot} /> Register the machines you brew on
                        </li>
                        <li>
                            <FontAwesomeIcon icon={faSeedling} /> Catalogue your favourite beans
                        </li>
                        <li>
                            <FontAwesomeIcon icon={faCapsules} /> Track the capsules you use
                        </li>
                        <li>
                            <FontAwesomeIcon icon={faStar} /> Rate home coffees with the gear attached
                        </li>
                    </ul>

                    {isPending ? (
                        <div className="mm-pending">
                            <FontAwesomeIcon icon={faHourglassHalf} />
                            <span>Request pending — the admin will review it.</span>
                        </div>
                    ) : (
                        <>
                            {isRejected && (
                                <p className="mm-rejected-note">
                                    Your previous request wasn't approved. You can send another one below.
                                </p>
                            )}
                            <label className="mm-field">
                                <span>Message (optional)</span>
                                <textarea
                                    value={requestMessage}
                                    onChange={(e) => setRequestMessage(e.target.value)}
                                    placeholder="Tell the admin a little about your coffee setup…"
                                    rows={3}
                                    maxLength={500}
                                />
                            </label>
                            <button
                                className="mm-btn mm-btn--primary mm-btn--block"
                                type="button"
                                onClick={handleRequestAccess}
                                disabled={requesting}
                            >
                                <FontAwesomeIcon icon={requesting ? faSpinner : faPaperPlane} spin={requesting} />
                                {requesting ? 'Sending…' : isRejected ? 'Request again' : 'Request access'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // ---------- render: master (full setup) ----------

    return (
        <div className="machine-manager">
            <header className="mm-header">
                <h2>
                    <FontAwesomeIcon icon={faCrown} /> Coffee Master
                </h2>
                <span className="mm-count">{activeCount} active</span>
            </header>
            <p className="mm-subtitle">My coffee setup</p>

            <div className="mm-tabs" role="tablist">
                <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'machines'}
                    className={`mm-tab ${tab === 'machines' ? 'is-active' : ''}`}
                    onClick={() => setTab('machines')}
                >
                    <FontAwesomeIcon icon={faMugHot} /> Machines
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'beans'}
                    className={`mm-tab ${tab === 'beans' ? 'is-active' : ''}`}
                    onClick={() => setTab('beans')}
                >
                    <FontAwesomeIcon icon={faSeedling} /> Beans
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'capsules'}
                    className={`mm-tab ${tab === 'capsules' ? 'is-active' : ''}`}
                    onClick={() => setTab('capsules')}
                >
                    <FontAwesomeIcon icon={faCapsules} /> Capsules
                </button>
            </div>

            {/* ---------------- MACHINES ---------------- */}
            {tab === 'machines' && (
                <div className="mm-section">
                    <form className="mm-card mm-form" onSubmit={handleMachineSubmit}>
                        <h3>Register a machine</h3>

                        <div className="mm-type-row">
                            {TYPE_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    className={`mm-type-btn ${machineForm.type === opt.value ? 'is-active' : ''}`}
                                    onClick={() => setMachineField('type', opt.value)}
                                >
                                    <span className="mm-type-label">{opt.label}</span>
                                    <span className="mm-type-hint">{opt.hint}</span>
                                </button>
                            ))}
                        </div>

                        <div className="mm-field-grid">
                            <label className="mm-field">
                                <span>Brand *</span>
                                <input
                                    type="text"
                                    value={machineForm.brand}
                                    onChange={(e) => setMachineField('brand', e.target.value)}
                                    placeholder="e.g. Gaggia, Nespresso"
                                    maxLength={80}
                                />
                            </label>
                            <label className="mm-field">
                                <span>Model *</span>
                                <input
                                    type="text"
                                    value={machineForm.model}
                                    onChange={(e) => setMachineField('model', e.target.value)}
                                    placeholder="e.g. Classic Pro, Vertuo"
                                    maxLength={120}
                                />
                            </label>
                        </div>

                        {machineForm.type === 'manual' && (
                            <div className="mm-field-grid">
                                <label className="mm-field">
                                    <span>Default grind</span>
                                    <input
                                        type="text"
                                        value={machineForm.grind}
                                        onChange={(e) => setMachineField('grind', e.target.value)}
                                        placeholder="e.g. fine, 18 clicks"
                                        maxLength={80}
                                    />
                                </label>
                                <label className="mm-field">
                                    <span>Default dose (g)</span>
                                    <input
                                        type="number"
                                        value={machineForm.doseGrams}
                                        onChange={(e) => setMachineField('doseGrams', e.target.value)}
                                        placeholder="e.g. 18"
                                        min={0}
                                        step="0.5"
                                    />
                                </label>
                            </div>
                        )}

                        {machineForm.type === 'capsule' && (
                            <div className="mm-field-grid">
                                <label className="mm-field">
                                    <span>Default capsule brand</span>
                                    <input
                                        type="text"
                                        value={machineForm.capsuleBrand}
                                        onChange={(e) => setMachineField('capsuleBrand', e.target.value)}
                                        placeholder="e.g. Nespresso Original"
                                        maxLength={80}
                                    />
                                </label>
                                <label className="mm-field">
                                    <span>Default intensity</span>
                                    <input
                                        type="number"
                                        value={machineForm.intensity}
                                        onChange={(e) => setMachineField('intensity', e.target.value)}
                                        placeholder="e.g. 8"
                                        min={0}
                                        step={1}
                                    />
                                </label>
                            </div>
                        )}

                        {machineForm.type === 'other' && (
                            <label className="mm-field">
                                <span>Notes</span>
                                <textarea
                                    value={machineForm.notes}
                                    onChange={(e) => setMachineField('notes', e.target.value)}
                                    placeholder="Anything worth remembering about this machine"
                                    rows={3}
                                    maxLength={500}
                                />
                            </label>
                        )}

                        <button className="mm-btn mm-btn--primary mm-btn--block" type="submit" disabled={submittingMachine}>
                            <FontAwesomeIcon icon={submittingMachine ? faSpinner : faPlus} spin={submittingMachine} />
                            {submittingMachine ? 'Registering…' : 'Register machine'}
                        </button>
                    </form>

                    <section className="mm-list">
                        {activeMachines.length === 0 && inactiveMachines.length === 0 && (
                            <p className="mm-empty">No machines yet — register your first one above.</p>
                        )}

                        {activeMachines.map((m) => (
                            <div className="mm-card mm-item" key={m._id}>
                                <div className="mm-item-main">
                                    <span className={`mm-tag mm-tag--${m.type}`}>{m.type}</span>
                                    <div className="mm-item-name">
                                        <strong>{m.brand}</strong> {m.model}
                                    </div>
                                </div>
                                <button
                                    className="mm-btn mm-btn--danger"
                                    type="button"
                                    onClick={() => handleDeactivateMachine(m)}
                                    disabled={deactivatingId === m._id}
                                >
                                    <FontAwesomeIcon
                                        icon={deactivatingId === m._id ? faSpinner : faPowerOff}
                                        spin={deactivatingId === m._id}
                                    />
                                    Deactivate
                                </button>
                            </div>
                        ))}

                        {inactiveMachines.length > 0 && (
                            <>
                                <h4 className="mm-subhead">Inactive</h4>
                                {inactiveMachines.map((m) => (
                                    <div className="mm-card mm-item mm-item--inactive" key={m._id}>
                                        <div className="mm-item-main">
                                            <span className={`mm-tag mm-tag--${m.type}`}>{m.type}</span>
                                            <div className="mm-item-name">
                                                <strong>{m.brand}</strong> {m.model}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </section>
                </div>
            )}

            {/* ---------------- BEANS ---------------- */}
            {tab === 'beans' && (
                <div className="mm-section">
                    <p className="mm-note">
                        Beans aren't rated directly — their score comes from the home coffees you log with them.
                    </p>

                    <form className="mm-card mm-form" onSubmit={handleBeanSubmit}>
                        <h3>Add a bean</h3>

                        <label className="mm-field">
                            <span>Name *</span>
                            <input
                                type="text"
                                value={beanForm.name}
                                onChange={(e) => setBeanField('name', e.target.value)}
                                placeholder="e.g. Ethiopia Yirgacheffe"
                                maxLength={120}
                            />
                        </label>

                        <div className="mm-field-grid">
                            <label className="mm-field">
                                <span>Roaster</span>
                                <input
                                    type="text"
                                    value={beanForm.roaster}
                                    onChange={(e) => setBeanField('roaster', e.target.value)}
                                    placeholder="e.g. Taf, Square Mile"
                                    maxLength={80}
                                />
                            </label>
                            <label className="mm-field">
                                <span>Origin</span>
                                <input
                                    type="text"
                                    value={beanForm.origin}
                                    onChange={(e) => setBeanField('origin', e.target.value)}
                                    placeholder="e.g. Ethiopia, Brazil"
                                    maxLength={80}
                                />
                            </label>
                        </div>

                        <div className="mm-field-grid">
                            <label className="mm-field">
                                <span>Roast level</span>
                                <select
                                    value={beanForm.roastLevel}
                                    onChange={(e) => setBeanField('roastLevel', e.target.value)}
                                >
                                    <option value="">—</option>
                                    {ROAST_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="mm-field">
                                <span>Process</span>
                                <input
                                    type="text"
                                    value={beanForm.process}
                                    onChange={(e) => setBeanField('process', e.target.value)}
                                    placeholder="e.g. washed, natural"
                                    maxLength={80}
                                />
                            </label>
                        </div>

                        <label className="mm-field">
                            <span>Notes</span>
                            <textarea
                                value={beanForm.notes}
                                onChange={(e) => setBeanField('notes', e.target.value)}
                                placeholder="Tasting notes, anything worth remembering"
                                rows={3}
                                maxLength={500}
                            />
                        </label>

                        <button className="mm-btn mm-btn--primary mm-btn--block" type="submit" disabled={submittingBean}>
                            <FontAwesomeIcon icon={submittingBean ? faSpinner : faPlus} spin={submittingBean} />
                            {submittingBean ? 'Adding…' : 'Add bean'}
                        </button>
                    </form>

                    <section className="mm-list">
                        {activeBeans.length === 0 && inactiveBeans.length === 0 && (
                            <p className="mm-empty">No beans yet — add your first one above.</p>
                        )}

                        {activeBeans.map((b) => (
                            <div className="mm-card mm-item" key={b._id}>
                                <div className="mm-item-main">
                                    <span className="mm-tag mm-tag--bean">
                                        <FontAwesomeIcon icon={faSeedling} />
                                    </span>
                                    <div className="mm-item-body">
                                        <div className="mm-item-name">
                                            <strong>{b.name}</strong>
                                            {b.roaster ? ` · ${b.roaster}` : ''}
                                        </div>
                                        <div className="mm-item-meta">
                                            {[b.origin, b.roastLevel, b.process].filter(Boolean).join(' · ')}
                                        </div>
                                        <ScorePill avgRating={b.avgRating} ratingCount={b.ratingCount} />
                                    </div>
                                </div>
                                <button
                                    className="mm-btn mm-btn--danger"
                                    type="button"
                                    onClick={() => handleDeactivateBean(b)}
                                    disabled={deactivatingId === b._id}
                                >
                                    <FontAwesomeIcon
                                        icon={deactivatingId === b._id ? faSpinner : faPowerOff}
                                        spin={deactivatingId === b._id}
                                    />
                                    Deactivate
                                </button>
                            </div>
                        ))}

                        {inactiveBeans.length > 0 && (
                            <>
                                <h4 className="mm-subhead">Inactive</h4>
                                {inactiveBeans.map((b) => (
                                    <div className="mm-card mm-item mm-item--inactive" key={b._id}>
                                        <div className="mm-item-main">
                                            <span className="mm-tag mm-tag--bean">
                                                <FontAwesomeIcon icon={faSeedling} />
                                            </span>
                                            <div className="mm-item-body">
                                                <div className="mm-item-name">
                                                    <strong>{b.name}</strong>
                                                    {b.roaster ? ` · ${b.roaster}` : ''}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </section>
                </div>
            )}

            {/* ---------------- CAPSULES ---------------- */}
            {tab === 'capsules' && (
                <div className="mm-section">
                    <p className="mm-note">
                        Capsules aren't rated directly — their score comes from the home coffees you log with them.
                    </p>

                    <form className="mm-card mm-form" onSubmit={handleCapsuleSubmit}>
                        <h3>Add a capsule</h3>

                        <div className="mm-field-grid">
                            <label className="mm-field">
                                <span>Brand *</span>
                                <input
                                    type="text"
                                    value={capsuleForm.brand}
                                    onChange={(e) => setCapsuleField('brand', e.target.value)}
                                    placeholder="e.g. Nespresso, Lavazza"
                                    maxLength={80}
                                />
                            </label>
                            <label className="mm-field">
                                <span>Name *</span>
                                <input
                                    type="text"
                                    value={capsuleForm.name}
                                    onChange={(e) => setCapsuleField('name', e.target.value)}
                                    placeholder="e.g. Arpeggio, Ristretto"
                                    maxLength={120}
                                />
                            </label>
                        </div>

                        <div className="mm-field-grid">
                            <label className="mm-field">
                                <span>Intensity</span>
                                <input
                                    type="number"
                                    value={capsuleForm.intensity}
                                    onChange={(e) => setCapsuleField('intensity', e.target.value)}
                                    placeholder="e.g. 8"
                                    min={0}
                                    step={1}
                                />
                            </label>
                            <div className="mm-field mm-field--spacer" aria-hidden="true" />
                        </div>

                        <label className="mm-field">
                            <span>Notes</span>
                            <textarea
                                value={capsuleForm.notes}
                                onChange={(e) => setCapsuleField('notes', e.target.value)}
                                placeholder="Tasting notes, anything worth remembering"
                                rows={3}
                                maxLength={500}
                            />
                        </label>

                        <button className="mm-btn mm-btn--primary mm-btn--block" type="submit" disabled={submittingCapsule}>
                            <FontAwesomeIcon icon={submittingCapsule ? faSpinner : faPlus} spin={submittingCapsule} />
                            {submittingCapsule ? 'Adding…' : 'Add capsule'}
                        </button>
                    </form>

                    <section className="mm-list">
                        {activeCapsules.length === 0 && inactiveCapsules.length === 0 && (
                            <p className="mm-empty">No capsules yet — add your first one above.</p>
                        )}

                        {activeCapsules.map((c) => (
                            <div className="mm-card mm-item" key={c._id}>
                                <div className="mm-item-main">
                                    <span className="mm-tag mm-tag--capsule">
                                        <FontAwesomeIcon icon={faCapsules} />
                                    </span>
                                    <div className="mm-item-body">
                                        <div className="mm-item-name">
                                            <strong>{c.brand}</strong> {c.name}
                                        </div>
                                        <div className="mm-item-meta">
                                            {c.intensity != null ? `intensity ${c.intensity}` : ''}
                                        </div>
                                        <ScorePill avgRating={c.avgRating} ratingCount={c.ratingCount} />
                                    </div>
                                </div>
                                <button
                                    className="mm-btn mm-btn--danger"
                                    type="button"
                                    onClick={() => handleDeactivateCapsule(c)}
                                    disabled={deactivatingId === c._id}
                                >
                                    <FontAwesomeIcon
                                        icon={deactivatingId === c._id ? faSpinner : faPowerOff}
                                        spin={deactivatingId === c._id}
                                    />
                                    Deactivate
                                </button>
                            </div>
                        ))}

                        {inactiveCapsules.length > 0 && (
                            <>
                                <h4 className="mm-subhead">Inactive</h4>
                                {inactiveCapsules.map((c) => (
                                    <div className="mm-card mm-item mm-item--inactive" key={c._id}>
                                        <div className="mm-item-main">
                                            <span className="mm-tag mm-tag--capsule">
                                                <FontAwesomeIcon icon={faCapsules} />
                                            </span>
                                            <div className="mm-item-body">
                                                <div className="mm-item-name">
                                                    <strong>{c.brand}</strong> {c.name}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </section>
                </div>
            )}
        </div>
    );
};

export default MachineManager;
